import { HOME_SECTIONS } from "../lib/routes";
import { StaticNote, StaticPage, StaticSection } from "./StaticPage";

/**
 * The Disclosure policy (`WEB-018`).
 *
 * Canon § 12 requires the paid basis of the daily ranking to be visible; the board's brass
 * band is where that duty is discharged in the moment, and this is the standing statement a
 * reader can reach from anywhere. Canon § 11 and § 14 fix the other half — what money does
 * *not* buy — and canon § 2 and § 3 fix the gate that runs before any of it.
 *
 * Every sentence here is canon this repository already holds. Nothing on this page is a
 * commitment Dealbid has not already made in `CANON.md`, and in particular nothing on it
 * promises a founder sales, leads, sign-ups or a number of impressions: canon § 1 declines
 * that promise in the product thesis, and a policy page is exactly where it would creep
 * back in as reassurance.
 *
 * `Deal Verified` is stated in canon § 3's own terms, positive claim and limits together.
 * Splitting them across a page — the badge's meaning here, its limits somewhere quieter —
 * is how a review becomes read as an endorsement.
 */
export function DisclosurePolicy() {
  return (
    <StaticPage
      kicker="Policy"
      title="Disclosure policy"
      lede={
        <>
          Dealbid sells one thing: a day of placement on the board. This page states which
          parts of this site are paid for, which parts are decided by buyers, and what the
          review behind a listing did and did not check.
        </>
      }
    >
      <StaticSection id="sponsored-placement" title="The daily Top 10 is sponsored placement">
        <p>
          Every position on <a href={HOME_SECTIONS.today}>Today&rsquo;s Top 10</a> was won in
          an auction. Founders bid one day for the next day&rsquo;s board, each daily auction
          is independent, and bidding opens at $1. The highest qualifying bids take the ten
          positions, highest first; where two bids are equal, the one placed earlier ranks
          higher.
        </p>
        <p>
          A won position is held for the whole of the next display day and cannot be outbid
          during it. Where fewer than ten positions are won, previously featured deals fill
          the rest at no additional charge rather than leaving the board short. The board
          marks which placements those are.
        </p>
        <p>
          <a href={HOME_SECTIONS.honorable}>Honorable Mentions</a> are paid placement too, at
          a flat $1 for a single display day. They sit outside the Top 10, they never enter
          it by being bought, and they do not carry over to the next day.
        </p>
      </StaticSection>

      <StaticSection id="not-editorial" title="A high bid is not an editorial judgement">
        <p>
          Rank on the daily board reflects what was bid for the position, and nothing else.
          The deal at number one is the deal whose founder bid the most for that day &mdash;
          not the deal Dealbid considers the best, the cheapest or the most trustworthy.
          Nobody here orders the board by quality, and no amount of money buys a favourable
          review.
        </p>
        <p>
          Wherever the board appears, it says so. The disclosure sits between the heading and
          the first position rather than in a footnote, because a disclosure a reader has to
          go looking for is not one.
        </p>
      </StaticSection>

      <StaticSection id="earned-lists" title="The weekly and monthly lists cannot be bought">
        <p>
          <a href={HOME_SECTIONS.weekly}>Top Deals this week</a> and{" "}
          <a href={HOME_SECTIONS.monthly}>Top Deals this month</a> rank deals by qualified
          buyer clicks through to the seller&rsquo;s own site during that calendar week or
          month, with filtering for abuse. Bid amount plays no part in either, and there is
          no way to pay for a place on them.
        </p>
        <p>
          The separation is deliberate, and it is why the daily board and the earned lists
          can disagree with each other. A founder can buy a day of exposure. What buyers do
          with that day is not for sale.
        </p>
      </StaticSection>

      <StaticSection id="eligibility" title="Eligibility is decided before bidding">
        <p>
          Review comes first. A deal is checked and approved before it can be listed or bid
          at all, so an auction only ever orders deals that already qualify. A large bid
          cannot make an ineligible deal eligible, and it cannot reopen a rejected one.
        </p>
        <p>
          The bar is the offer itself: ordinarily a real discount of 30% or more, or value
          that is clearly compelling by some other measure. A nominal or misleading discount
          is not eligible, however much is bid for it.
        </p>
      </StaticSection>

      <StaticSection id="deal-verified" title="What Deal Verified means">
        <p>
          <strong>Deal Verified</strong> means the seller association and advertised deal
          information were checked at review time. Review is manual, and it looks at:
        </p>
        <ul>
          <li>that the product and its site exist and work well enough to evaluate;</li>
          <li>that the submitter appears legitimately associated with the product;</li>
          <li>that the deal and destination links work;</li>
          <li>
            that the advertised price or value is genuine on the evidence available at the
            time;
          </li>
          <li>that the limitations that matter are disclosed;</li>
          <li>that the offer is not deceptive, abusive or prohibited by policy.</li>
        </ul>
        <StaticNote title="What Deal Verified is not">
          <p>
            It is not a security audit, an endorsement, a solvency guarantee, or a promise
            that a lifetime-deal vendor will still exist in a year. Dealbid reviews a deal at
            a point in time; it does not underwrite the company behind it. You buy on the
            seller&rsquo;s own site, on the seller&rsquo;s own terms.
          </p>
        </StaticNote>
      </StaticSection>

      <StaticSection id="no-promises" title="What Dealbid does not promise a founder">
        <p>
          Placement is inventory, not a result. A winning bid buys a defined day on the
          board, and that is the whole of what is being sold: Dealbid does not promise
          sales, leads, sign-ups or a number of impressions, and it does not publish a figure
          it cannot measure honestly. What it does measure &mdash; placements, and qualified
          clicks through to the seller &mdash; is what this site shows.
        </p>
      </StaticSection>
    </StaticPage>
  );
}
